import { useEffect, useState } from "react";
import api from "../lib/api";
import { PageHeader, Stat, EmptyState } from "../components/common";
import { eur, itDate } from "../lib/constants";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Plus, Download, Trash2, Search } from "lucide-react";
import CashflowView from "../components/CashflowView";
import { useAuth } from "../context/AuthContext";

export default function Incassi() {
  const { can } = useAuth();
  const [tab, setTab] = useState("previsioni");
  const [payments, setPayments] = useState([]);
  const [clients, setClients] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [stats, setStats] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ kind: "acconto", method: "bonifico", date: new Date().toISOString().slice(0,10) });
  const [from, setFrom] = useState(""); const [to, setTo] = useState("");
  const [pq, setPq] = useState("");

  const load = async () => {
    const q = from && to ? `?date_from=${from}&date_to=${to}` : "";
    const [p, s] = await Promise.all([api.get(`/payments${q}`), api.get(`/stats/payments${q}`)]);
    setPayments(p.data); setStats(s.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [from, to]);
  useEffect(() => { api.get("/clients").then(r=>setClients(r.data)); api.get("/jobs").then(r=>setJobs(r.data)); }, []);

  // Safety net for Radix Dialog+Select leaving body non-interactive on some mobile browsers
  useEffect(() => {
    if (!open) {
      const t = setTimeout(() => { if (document.body.style.pointerEvents === "none") document.body.style.pointerEvents = ""; }, 200);
      return () => clearTimeout(t);
    }
  }, [open]);

  const save = async () => {
    if (!form.client_id || !form.amount) return toast.error("Cliente e importo obbligatori");
    await api.post("/payments", form); toast.success("Incasso registrato"); setOpen(false);
    setForm({ kind: "acconto", method: "bonifico", date: new Date().toISOString().slice(0,10) }); load();
  };
  const del = async (id) => { await api.delete(`/payments/${id}`); load(); };

  return (
    <div>
      <PageHeader title="Incassi e spese" subtitle={tab === "previsioni" ? "Entrate e uscite previste, mese per mese" : "Pagamenti realmente ricevuti · schema 40/40/20"}>
        {tab === "registro" && <a href={`${api.defaults.baseURL}/export/payments.csv`} target="_blank" rel="noreferrer"><Button variant="outline"><Download size={16} className="mr-1" /> CSV</Button></a>}
        {tab === "registro" && <Button className="bg-brand hover:bg-[#A8472F] text-white" data-testid="btn-new-payment" onClick={() => setOpen(true)}><Plus size={16} className="mr-1" /> Registra incasso</Button>}
      </PageHeader>
      <div className="flex gap-2 mb-5">
        {[["previsioni", "Entrate e uscite previste"], ["registro", "Incassi registrati dai lavori"]].map(([k, l]) => (
          <button key={k} data-testid={`incassi-tab-${k}`} onClick={() => setTab(k)} className={`text-sm px-4 py-2 rounded-full border transition-colors ${tab === k ? "bg-brand text-white border-brand" : "bg-white"}`}>{l}</button>
        ))}
      </div>
      {tab === "previsioni" ? <CashflowView canEdit={can("edit_payments")} /> : (<>

      <div className="flex gap-3 items-end mb-5 flex-wrap">
        <div><Label className="text-xs">Dal</Label><Input type="date" value={from} onChange={(e)=>setFrom(e.target.value)} /></div>
        <div><Label className="text-xs">Al</Label><Input type="date" value={to} onChange={(e)=>setTo(e.target.value)} /></div>
        {(from||to) && <Button variant="ghost" size="sm" onClick={()=>{setFrom("");setTo("");}}>Azzera</Button>}
        <div className="relative flex-1 min-w-[220px]"><Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="payments-search" className="pl-9" placeholder="Cerca cliente, importo, data, metodo, note…" value={pq} onChange={(e) => setPq(e.target.value)} /></div>
      </div>

      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
          <Stat label="Totale incassato" value={eur(stats.total_received)} tone="green" testid="stat-received" />
          <Stat label="Numero pagamenti" value={stats.count} />
          <Stat label="Mesi con incassi" value={Object.keys(stats.by_month||{}).length} />
        </div>
      )}

      {payments.length === 0 ? <EmptyState title="Nessun incasso" hint="Un preventivo accettato NON è denaro incassato." /> : (
        <div className="space-y-2">
          {payments.filter((p) => { const s = pq.trim().toLowerCase(); return !s || [p.client_name, p.notes, p.method, p.kind, p.date, itDate(p.date), (p.date || "").split("-").reverse().join("/"), String(p.amount), eur(p.amount), Number(p.amount).toFixed(2).replace(".", ",")].some((x) => (x || "").toString().toLowerCase().includes(s)); }).map((p) => (
            <div key={p.id} data-testid={`payment-${p.id}`} className="card-soft p-3 flex items-center justify-between">
              <div><p className="font-medium text-sm">{p.client_name} · <span className="text-brand">{eur(p.amount)}</span></p>
                <p className="text-xs text-muted-foreground">{itDate(p.date)} · {p.method} · {p.kind} {p.notes && `· ${p.notes}`}</p></div>
              <button onClick={() => del(p.id)} className="text-destructive"><Trash2 size={16} /></button>
            </div>
          ))}
        </div>
      )}
      </>)}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="payment-dialog">
          <DialogHeader><DialogTitle className="font-head">Registra incasso</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Cliente *</Label>
              <Select value={form.client_id || ""} onValueChange={(v)=>setForm({...form,client_id:v})}>
                <SelectTrigger data-testid="payment-client"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                <SelectContent onCloseAutoFocus={(e) => e.preventDefault()}>{clients.filter((c)=>c.id).map((c)=><SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Lavoro</Label>
              <Select value={form.job_id || ""} onValueChange={(v)=>setForm({...form,job_id:v})}>
                <SelectTrigger><SelectValue placeholder="(opzionale)" /></SelectTrigger>
                <SelectContent onCloseAutoFocus={(e) => e.preventDefault()}>{jobs.filter(j=>j.id && (!form.client_id||j.client_id===form.client_id)).map((j)=><SelectItem key={j.id} value={j.id}>{j.product}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Importo € *</Label><Input type="number" data-testid="payment-amount" value={form.amount||""} onChange={(e)=>setForm({...form,amount:e.target.value})} /></div>
              <div><Label>Data</Label><Input type="date" value={form.date} onChange={(e)=>setForm({...form,date:e.target.value})} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Tipo</Label>
                <Select value={form.kind} onValueChange={(v)=>setForm({...form,kind:v})}>
                  <SelectTrigger data-testid="payment-kind"><SelectValue /></SelectTrigger>
                  <SelectContent>{["acconto","saldo","correzione","rimborso"].map(k=><SelectItem key={k} value={k} className="capitalize">{k}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Modalità</Label>
                <Select value={form.method} onValueChange={(v)=>setForm({...form,method:v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{["bonifico","contanti","assegno","carta","altro"].map(k=><SelectItem key={k} value={k} className="capitalize">{k}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Note</Label><Textarea value={form.notes||""} onChange={(e)=>setForm({...form,notes:e.target.value})} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={()=>setOpen(false)}>Annulla</Button><Button className="bg-brand text-white" data-testid="payment-save" onClick={save}>Salva</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
